import { tokens } from "@core/theme/tokens";
import { EmptyState, FeedbackState } from "@portal/common";
import { formatShortDate } from "../vehicles/dateFormat";
import type { MemberMaintenance } from "./api";
import { useMemberMaintenances } from "./maintenanceQueries";

// Mismo criterio de formateo que `vehicles/MaintenancePanel.tsx`
// (`formatMileage`): no se exporta desde ahí, así que se repite acá en vez de
// mover un formateador de presentación a `core` (no es compartido con el
// validador).
const maintenanceMileageFormatter = new Intl.NumberFormat("es-EC");
const formatMileage = (value?: number | null) =>
  value != null ? `${maintenanceMileageFormatter.format(value)} km` : "—";

// SPEC-203 §4 (resolución humana 2026-09-19): esta pestaña es de solo
// lectura — ningún botón de registrar/editar acá, eso sigue siendo por
// vehículo en `vehicles/MaintenancePanel.tsx`. La tarjeta de cada fila reusa
// el mismo layout (punto + tarjeta con grilla de 4 datos) que ese panel, solo
// agregando el vehículo al que pertenece — el prototipo no dibuja esta
// pestaña (no hay baseline propio), así que el look sale de ese panel
// hermano, no de un estilo nuevo.
export function MemberMaintenancesPanel({ memberId }: { memberId: string }) {
  const maintenances = useMemberMaintenances(memberId);
  const rows = maintenances.data ?? [];

  return (
    <div data-testid="member-maintenances-panel">
      {maintenances.isLoading ? (
        <FeedbackState kind="loading" />
      ) : maintenances.isError ? (
        <EmptyState kind="error" />
      ) : rows.length ? (
        rows.map((m: MemberMaintenance, index: number) => (
          <div key={m.id} style={{ display: "flex", gap: 14 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: 12,
                flex: "none",
                paddingTop: 20,
              }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  flex: "none",
                  background:
                    m.status === "completed"
                      ? tokens.gold.base
                      : tokens.silver.flat,
                }}
              />
              <span
                style={{
                  flex: 1,
                  width: 1,
                  background:
                    index === rows.length - 1
                      ? "transparent"
                      : tokens.surface.line,
                }}
              />
            </div>
            <div
              style={{
                flex: 1,
                marginBottom: 12,
                padding: 16,
                background: tokens.surface.surface,
                border: `1px solid ${tokens.surface.line}`,
                borderRadius: tokens.radius.sm,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <div>
                  <span
                    style={{
                      font: `600 14px ${tokens.font.ui}`,
                      color: tokens.text.primary,
                    }}
                  >
                    {m.type}
                  </span>
                  <div
                    style={{
                      font: `400 11.5px ${tokens.font.ui}`,
                      color: tokens.text.tertiary,
                      marginTop: 3,
                    }}
                  >
                    {m.vehicle.model} · {m.vehicle.plate}
                  </div>
                </div>
                <span
                  className={`stat stat-${m.status === "completed" ? "gold" : "silver"}`}
                >
                  {m.status === "completed" ? "Realizado" : "Programado"}
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px 16px",
                  marginTop: 13,
                }}
              >
                {(
                  [
                    ["Fecha estimada", formatShortDate(m.estimatedDate)],
                    ["Fecha real", formatShortDate(m.actualDate)],
                    ["Kilometraje", formatMileage(m.mileage)],
                    ["Taller", m.workshop ?? "—"],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label}>
                    <div
                      className="label"
                      style={{ fontSize: 9.5, marginBottom: 3 }}
                    >
                      {label}
                    </div>
                    <div
                      style={{
                        font: `400 12px ${tokens.font.ui}`,
                        color: tokens.text.secondary,
                      }}
                    >
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))
      ) : (
        <EmptyState kind="no-data" />
      )}
    </div>
  );
}
